import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from '../../../components/Whatsapp/Template/template.constant.js';

export function isWhatsappTemplate(template) {
  if (!template) return false;
  const source = template.source_name || template.source;
  return (
    template.messaging_product === 'whatsapp' ||
    source === 'whatsapp' ||
    source === 'trustsignal' ||
    template.channel === 'whatsapp' ||
    template.platform === 'whatsapp' ||
    Boolean(template.ts_id || template.components || template.name)
  );
}

export function getTemplateComponents(template) {
  const raw = template?.components;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') return Object.values(parsed);
    } catch {
      return [];
    }
  }
  if (raw && typeof raw === 'object') {
    return Object.values(raw);
  }
  return [];
}

export function getHeaderComponent(template) {
  return getTemplateComponents(template).find((component) => component?.type === 'HEADER') || null;
}

export function initBodyParams(template) {
  const bodyObj = getTemplateComponents(template).find((component) => component?.type === 'BODY');
  if (!bodyObj?.example?.body_text?.length) return [];

  const params = bodyObj.example.body_text[0];
  return params.map((label) => ({ type: 'text', text: '', label }));
}

export function initButtonUrlParams(template) {
  const buttonObj = getTemplateComponents(template).find((component) => component?.type === 'BUTTONS');
  if (!buttonObj?.buttons?.length) return [];
  return buttonObj.buttons.map(() => null);
}

export function getButtonsWithExamples(template) {
  const buttonObj = getTemplateComponents(template).find((component) => component?.type === 'BUTTONS');
  if (!buttonObj?.buttons?.length) return [];
  return buttonObj.buttons
    .map((btn, originalIndex) => ({ ...btn, originalIndex }))
    .filter((btn) => Array.isArray(btn?.example) && btn.example.length > 0);
}

const PHONE_REGEX = /^([1-9]{1}[0-9]{1,12})$/;

export function validateWhatsappTemplateForm({
  phoneNumber,
  bodyParams = [],
  buttonUrlParams = [],
  buttonsWithExamples = [],
  headerComponent,
  headerFile,
  useCreateImage,
}) {
  const errors = {};

  if (!PHONE_REGEX.test(phoneNumber) || phoneNumber.length < 10) {
    errors.phone_number = 'Please enter a valid number';
  }

  bodyParams.forEach((item, index) => {
    if (!item?.text || !`${item.text}`.trim()) errors[`bodyParams_${index}`] = 'This field is required';
  });

  buttonsWithExamples.forEach((button) => {
    const index = button.originalIndex ?? 0;
    const value = buttonUrlParams[index];
    if (value == null || `${value}`.trim() === '') {
      errors[`buttonUrlParams_${index}`] = 'This field is required';
    }
  });

  if (
    headerComponent &&
    headerComponent.format !== 'TEXT' &&
    !headerFile &&
    !useCreateImage
  ) {
    errors.headerFile = 'This field is required';
  }

  return errors;
}

const EXT_TO_TYPE = {
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  webp: 'image',
  mp4: 'video',
  '3gp': 'video',
  '3gpp': 'video',
  mov: 'video',
  pdf: 'file',
  doc: 'file',
  docx: 'file',
  xls: 'file',
  xlsx: 'file',
  txt: 'file',
  csv: 'file',
  ppt: 'file',
  pptx: 'file',
  aac: 'audio',
  mp3: 'audio',
  mpeg: 'audio',
  amr: 'audio',
  ogg: 'audio',
  webm: 'audio',
  wav: 'audio',
};

function inferDocType(mime, ext) {
  const cleanMime = (mime || '').toLowerCase().split(';')[0].trim();
  if (WHATSAPP_ALLOWED_MIME.image.includes(cleanMime)) return 'image';
  if (WHATSAPP_ALLOWED_MIME.video.includes(cleanMime)) return 'video';
  if (WHATSAPP_ALLOWED_MIME.audio.includes(cleanMime)) return 'audio';
  if (WHATSAPP_ALLOWED_MIME.file.includes(cleanMime)) return 'file';

  const cleanExt = (ext || '').toLowerCase().replace(/^\./, '');
  if (EXT_TO_TYPE[cleanExt]) return EXT_TO_TYPE[cleanExt];

  return null;
}

function bytesToMB(bytes) {
  return (bytes / (1024 * 1024)).toFixed(2);
}

export function validateHeaderFile(file) {
  if (!file) return { valid: false, message: 'No file selected' };

  if (file.size <= 0) {
    return {
      valid: false,
      message: 'File cannot be 0 MB (empty file). Please select a valid file.',
    };
  }

  const fileName = file.name?.toLowerCase() || '';
  const ext = fileName.includes('.') ? `.${fileName.split('.').pop()}` : '';

  if (BLOCKED_EXTENSIONS.has(ext)) {
    return { valid: false, message: `"${ext}" files are not allowed for security reasons.` };
  }

  const typeGroup = inferDocType(file.type, ext);
  if (!typeGroup) {
    return { valid: false, message: `Unsupported file type (${file.type || ext}).` };
  }

  const limit = WHATSAPP_LIMITS[typeGroup];
  if (file.size > limit) {
    const typeLabel = typeGroup === 'file' ? 'document' : typeGroup;
    const limitMB = Math.round(limit / (1024 * 1024));
    return {
      valid: false,
      message: `File too large for ${typeLabel}. Max ${limitMB} MB. Your file is ${bytesToMB(file.size)} MB.`,
    };
  }

  return { valid: true };
}

export function getHeaderAcceptValue(format) {
  if (format === 'IMAGE') return 'image/jpeg,image/png';
  if (format === 'VIDEO') return 'video/mp4,video/3gpp';
  if (format === 'DOCUMENT') {
    return '.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv';
  }
  return '*/*';
}

export function buildButtonComponents(buttonUrlParams = [], template) {
  const buttonsWithExamples = getButtonsWithExamples(template);
  if (!buttonsWithExamples.length) return [];

  return buttonsWithExamples
    .map((button) => {
      const index = button.originalIndex ?? 0;
      const value = buttonUrlParams[index];
      if (value == null || `${value}`.trim() === '') return null;

      if (button.type === 'URL') {
        return {
          type: 'button',
          sub_type: 'url',
          index: `${index}`,
          parameters: [{ type: 'text', text: `${value}`.trim() }],
        };
      }

      return null;
    })
    .filter(Boolean);
}

export function buildTemplateSendPayload({
  phoneNumber,
  template,
  bodyParams = [],
  buttonUrlParams = [],
  headerParameters,
}) {
  const cleanPhone = (phoneNumber || '').toString().replace(/\D/g, '');
  const finalPhone = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;

  const components = [];

  if (headerParameters && headerParameters.length > 0) {
    components.push({ type: 'header', parameters: headerParameters });
  }

  const validBodyParams = bodyParams
    .filter((p) => p && p.text !== undefined && p.text !== null && `${p.text}`.trim() !== '')
    .map(({ type, text }) => ({ type: type || 'text', text: `${text}`.trim() }));

  if (validBodyParams.length > 0) {
    components.push({
      type: 'body',
      parameters: validBodyParams,
    });
  }

  const buttonComponents = buildButtonComponents(buttonUrlParams, template);
  if (buttonComponents.length) {
    components.push(...buttonComponents);
  }

  const payload = {
    phone_number: finalPhone,
    ts_id: template?.ts_id || template?.id || '',
    name: template.name,
    code: template.language || template.code || template.language_code || 'en',
    components,
  };

  return payload;
}

export function buildHeaderParametersFromUpload(headerComponent, uploadResponse) {
  const rawData = uploadResponse?.data?.data || uploadResponse?.data || {};
  const s3Obj = rawData?.s3_object || rawData;
  const link = s3Obj?.url || s3Obj?.link || rawData?.url || '';
  const rawName = link ? link.split('?')[0].split('/').pop() : '';
  const cleanFromLink = rawName.includes('_') ? rawName.slice(rawName.indexOf('_') + 1) : rawName;
  let caption = cleanFromLink || s3Obj?.filename || s3Obj?.name || rawData?.filename || 'document';
  try {
    caption = decodeURIComponent(caption);
  } catch (_) {}
  const formatType = (headerComponent?.format || 'document').toLowerCase();

  return [
    {
      type: formatType,
      caption,
      link,
    },
  ];
}

export function buildHeaderParametersFromMedia(headerComponent, template) {
  const imageUrl = template?.media?.header;
  if (!imageUrl) return null;

  const formatType = (headerComponent?.format || 'document').toLowerCase();
  const urlFilename = imageUrl.split('?')[0].split('/').pop() || '';
  const cleanFilename = urlFilename.includes('_')
    ? urlFilename.slice(urlFilename.indexOf('_') + 1)
    : urlFilename;
  let decodedFilename = cleanFilename;
  try {
    decodedFilename = decodeURIComponent(cleanFilename);
  } catch (_) {}
  const caption =
    formatType === 'image'
      ? 'autogenerated_caption.jpeg'
      : (decodedFilename || template?.media?.headerFilename || `header.${formatType}`);

  return [
    {
      type: formatType,
      caption,
      link: imageUrl,
    },
  ];
}

export function parseTemplateSendError(response) {
  const root = response?.response?.data || response?.data || response;
  const errorData = root?.data || root;
  const generalError = errorData?.general_error || root?.general_error;

  if (typeof generalError === 'string' && generalError.trim()) return generalError;
  if (generalError?.error?.message) {
    const details = generalError?.error?.error_data?.details;
    return details ? `${generalError.error.message} (${details})` : generalError.error.message;
  }
  if (generalError?.message) return generalError.message;
  if (typeof errorData?.error === 'string' && errorData.error.trim()) return errorData.error;
  if (errorData?.error?.message) return errorData.error.message;
  if (root?.message) return root.message;
  if (root?.detail) return root.detail;
  if (typeof errorData === 'string' && errorData.trim()) return errorData;
  return 'Failed to send template';
}

function fillTemplatePlaceholders(text, bodyParams = []) {
  if (!text) return '';
  const values = bodyParams.map((param) => param?.text || '');
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (_match, indexStr) => {
    const index = parseInt(indexStr, 10) - 1;
    const replacement = values[index];
    return replacement?.length ? replacement : `{{${indexStr}}}`;
  });
}

/**
 * Build a chat message shape for immediate Intervene display after template send.
 */
export function buildOptimisticTemplateMessage({
  phoneNumber,
  botNumber,
  template,
  bodyParams = [],
  headerParameters = null,
  msgId = null,
}) {
  const optimisticId = msgId || `local-template-${Date.now()}`;
  const now = new Date().toISOString();

  const content = {
    type: 'TEMPLATE',
    template_name: template?.name,
    header: { type: 'TEXT', text: '' },
    body: {
      type: 'TEXT',
      text: '',
      parameters: { global: [], result: [], webform: [] },
    },
    footer: { type: 'TEXT', text: '' },
    buttons: [],
  };

  getTemplateComponents(template).forEach((component) => {
    const compType = `${component?.type || ''}`.toUpperCase();

    if (compType === 'HEADER') {
      const headerParam = headerParameters?.[0];
      if (headerParam?.link) {
        content.header = {
          ...headerParam,
          type: `${headerParam.type || 'text'}`.toUpperCase(),
        };
      } else {
        content.header = { type: 'TEXT', text: component.text || '' };
      }
    } else if (compType === 'BODY') {
      content.body = {
        type: 'TEXT',
        text: fillTemplatePlaceholders(component.text || '', bodyParams),
        parameters: { global: [], result: [], webform: [] },
      };
    } else if (compType === 'FOOTER') {
      content.footer = { type: 'TEXT', text: component.text || '' };
    } else if (compType === 'BUTTONS') {
      const buttons = (component.buttons || [])
        .filter((button) => button?.type === 'QUICK_REPLY')
        .map((button) => ({
          label: button.text,
          value: button.text,
        }));
      if (buttons.length) {
        content.buttons = buttons;
        content.type = 'BUTTONS';
      }
    }
  });

  return {
    msg_id: optimisticId,
    w_id: optimisticId,
    created_at: now,
    updated_at: now,
    timestamp: Date.now(),
    origin: 'OUTBOUND',
    phone_number: phoneNumber,
    bot_number: botNumber,
    chat_type: 'bot',
    content,
    template_name: template?.name,
    status: { type: 'new message sent' },
  };
}

export function extractTemplateMessageFromSendResponse(response) {
  return (
    response?.data?.data?.message ||
    response?.data?.message ||
    null
  );
}
